import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Trash2, MessageSquare, Loader2, Presentation } from 'lucide-react';
import { ROLE_COLORS, ROLE_LABELS } from './CommunitySidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MembersTab = ({ 
  members, 
  currentUserId, 
  isAdmin, 
  availableRoles,
  communityId,
  onUpdateRole, 
  onRemoveMember 
}) => {
  const navigate = useNavigate();
  const [startingDM, setStartingDM] = useState(null);

  const handleStartDM = async (memberId) => {
    setStartingDM(memberId);
    try {
      const response = await axios.post(`${API}/dm/conversations`, {
        recipient_id: memberId,
        community_id: communityId
      });
      navigate(`/messages/${response.data.conversation_id}`);
    } catch (error) {
      toast.error('Failed to start conversation');
    } finally {
      setStartingDM(null);
    }
  };

  // Check if member can receive DMs (pastor, teacher, admin roles)
  const canReceiveDM = (member) => {
    if (member.user_id === currentUserId) return false;
    return ['pastor', 'teacher', 'admin', 'worship_leader'].includes(member.role);
  };

  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-2">
        {members.map((member) => (
          <div
            key={member.member_id}
            className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
            data-testid={`member-item-${member.user_id}`}
          >
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={member.picture} />
                <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{member.name}</p>
                  {/* Presenter Badge */}
                  {(member.has_presenter_access || member.user_type === 'presenter') && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge 
                          variant="outline" 
                          className="bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 px-1.5 py-0"
                          data-testid={`presenter-badge-${member.user_id}`}
                        >
                          <Presentation className="w-3 h-3" />
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Presenter - Can create & present slideshows</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{member.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* DM Button for pastors/teachers */}
              {canReceiveDM(member) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStartDM(member.user_id)}
                  disabled={startingDM === member.user_id}
                  className="text-pink-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-950/30"
                  data-testid={`dm-member-${member.user_id}`}
                >
                  {startingDM === member.user_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                </Button>
              )}
              
              {isAdmin && member.user_id !== currentUserId ? (
                <>
                  <Select 
                    value={member.role} 
                    onValueChange={(v) => onUpdateRole(member.user_id, v)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveMember(member.user_id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid={`remove-member-${member.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Badge className={ROLE_COLORS[member.role]}>
                  {ROLE_LABELS[member.role]}
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default MembersTab;
